import React, { useEffect, useLayoutEffect, useState } from "react";
import { getDaysDiff, getPubAbbr, formatThousand } from "@revbox/helpers-fe";
import { getPeriodCampaignImpressionsAndClicks } from "../../../services/micro";
import { Card, Placeholder, Col, Button } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import { campaignAlerts } from "@revbox/localization";
import { useCookiesContext } from "../../../contexts/CookiesContext";
import { subDays } from "date-fns";
import empty_state_clicks_iframe_icon from "../../../assets/svg/empty_state_clicks_iframe_icon.svg";
import { getCampaign } from "../../../services/adv";
import IframeBudgetAlert from "./IframeBudgetAlert";
import { blankPageMap } from "../../../modules/maps";
import { modules } from "../../../modules";

function getCSSVariableValue(publisherId, variableName) {
  if (publisherId == 34 || publisherId == 73) {
    if (variableName == '--chart-color-1') {
      return '#c4e3cc';
    } else if (variableName == '--chart-color-2') {
      return '#76CC8E';
    } else if (variableName == '--chart-color-3') {
      return '#3AB65D';
    }
  }

  if (publisherId == 67 || publisherId == 10) {
    if (variableName == '--chart-color-1') {
      return '#603eea41';
    } else if (variableName == '--chart-color-2') {
      return '#613EEA';
    } else if (variableName == '--chart-color-3') {
      return '#0cb785';
    }
  }

  return getComputedStyle(document.documentElement).getPropertyValue(variableName).trim();
}

export default function CampaignClicks(props) {
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get("token");
  const campaignId = urlParams.get("campaignId");

  const [currentPeriod, setCurrentPeriod] = useState([subDays(new Date(), 30), new Date()]);
  const [campaign, setCampaign] = useState(null);
  const [response, setResponse] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [colors, setColors] = useState({
    primary: "",
    chartSecondary: "",
    columnChart: "",
  });
  const [alertTitle, setAlertTitle] = useState('');
  const [alertText, setAlertText] = useState('');
  const { getLanguageAbbr } = useCookiesContext();
  const language = getLanguageAbbr();
  const publisherId = getPubAbbr()
  const alerts = campaignAlerts[language];

  useLayoutEffect(() => {
    
    const primary = getCSSVariableValue(publisherId, '--chart-color-3');
    const chartSecondary = getCSSVariableValue(publisherId,'--chart-color-2');
    const columnChart = getCSSVariableValue(publisherId,'--chart-color-1');
    setColors({ primary, chartSecondary, columnChart });
  }, [publisherId]);

  useEffect(() => {
    if (!currentPeriod[0] || !currentPeriod[1] || !campaignId || !token) return;
  
    let isCancelled = false; // for canceling the request if the component is unmounted
  
    const fetchData = async () => {
      try {
        const response = await getPeriodCampaignImpressionsAndClicks(currentPeriod, Number(campaignId), token);
        if (isCancelled) return;
        setResponse(response);
  
        const campaignRes = await getCampaign(campaignId, token);
        if (isCancelled) return;
        const campaignData = campaignRes?.data;
  
        setCampaign(campaignData);
  
        const { forecast_spend_persentage, forecast_spend_amount, ppc_paused, ppc_invoice_day, ppc_monthly_budget, ppc_monthly_spend } = campaignData || {};
  
        const isBudgetLowAlert = forecast_spend_persentage >= 110 && !ppc_paused && forecast_spend_amount > 0;
        const isBudgetUtilizationAlert = forecast_spend_persentage <= 85 && forecast_spend_persentage > 0 && !ppc_paused && forecast_spend_amount > 0;
  
        if (isBudgetLowAlert) {
          setAlertTitle(alerts.low_budget.title);
  
          const nextMonthDate = new Date();
          nextMonthDate.setMonth(nextMonthDate.getMonth() + 1);
          nextMonthDate.setDate(ppc_invoice_day);
  
          const daysDiff = getDaysDiff(ppc_invoice_day, new Date(), nextMonthDate);
          const numericPpcMonthlyBudget = parseFloat(ppc_monthly_budget.replace(/,/g, ''));
          const numericPpcMonthlySpend = parseFloat(ppc_monthly_spend.replace(/,/g, ''));

          const forecastSpendAmount = campaignData?.forecast_spend_amount ? parseFloat(campaignData?.forecast_spend_amount) : 0
          const forecastSpendDays = Math.floor((numericPpcMonthlyBudget - numericPpcMonthlySpend) / ((forecastSpendAmount - numericPpcMonthlySpend) / daysDiff))
      
          const today = new Date()
          const spendDate = new Date(today.setDate(today.getDate() + forecastSpendDays))
  
          const formattedDate = spendDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });

          setAlertText(alerts.low_budget.text(formattedDate));
        } 
        else if (isBudgetUtilizationAlert) {
          setAlertTitle(alerts.budget_utilization.title);
          setAlertText(alerts.budget_utilization.text(Math.round(forecast_spend_persentage)));
        }

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading campaign or impressions:', error);
      }
    };
  
    fetchData();
  
    return () => {
      isCancelled = true;
    };
  }, [currentPeriod[0], currentPeriod[1], campaignId, token]);
  
  const optionFour = {
    chart: {
      parentHeightOffset: 0,
      stacked: false,
      toolbar: { show: false },
      animations: {
        enabled: true,
      },
      type: "line", 
    },
    grid: {
      borderColor: "rgba(72,94,144, 0.07)",
      padding: {
        top: -20,
        bottom: -20,
        left: 20,
        right: 0,
      },
    },
    colors: [colors.columnChart, colors.chartSecondary, colors.primary], 
    plotOptions: {
      bar: { columnWidth: "50%" },
    },
    stroke: {
      curve: ["smooth", "smooth", "smooth"], 
      width: [1.5, 1.5, 2], 
    },
    fill: {
      type: ["solid", "gradient", "solid"],
      opacity: 1,
      gradient: {
        type: "vertical",
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0.2,
        stops: [0, 100],
        gradientToColors: ["#f3f5fc"], 
      },
    },
    xaxis: {
      tickAmount: 15,
      labels: {
        style: {
          colors: "#ccc",
          fontSize: "11px",
        },
        rotate: -45,
        rotateAlways: true, 
      },
    },
    yaxis: [
      {
        seriesName: "Clicks",
        opposite: true,
        labels: {
          show: false, 
        },
      }
    ],
    dataLabels: { enabled: false },
    tooltip: {
      shared: true,
      intersect: false
    },
    legend: { show: true },
  };

  const openCampaign = () => {
    // open it in the current tab, but be careful with it, because this component is used in iframe
    const url = `https://hoteltechreport.com/dashboard/${campaign?.external_id}/ppc#ppc-auction`;

    try {
      window.top.location.href = url;
    } catch (error) {
      window.location.href = url;
    }
  };

  if (response && response.stats) {
    response.stats = [response.stats[0]];

    const categories = response.dates;
    optionFour.xaxis.categories = categories;
    optionFour.xaxis.tickAmount = 15;

    const totalClicks = response.stats[0].data.reduce((a, b) => a + b, 0);

    // check if the every item of data is 0
    if (response.stats[0].data.every((item) => item === 0)) {
      // last 30 dates
      let empty_state_categories = [];

      for (let i = 0; i < 30; i++) {
        empty_state_categories.push(new Date(
          subDays(new Date(), i)
        ).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
      }

      optionFour.xaxis.categories = empty_state_categories

      return (
        <React.Fragment>
          <Col md="12" xl="12">
            <Card className="card-one iframe-card-background" style={{ 
              borderColor: '#E9E9ED', 
              shadow: '0px 1px 2px rgba(0, 0, 0, 0.06)',  
              borderRadius: '8px',
              padding: '0px',
              height: '387px',
              border: '1px solid #E9E9ED',
              backgroundColor: '#FFFFFF',
            }}>
              <Card.Body style={{ padding: '0px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '340px',
                  width: '100%',
                  position: 'absolute',
                }}>
                  <div
                    style={{
                      backgroundColor: '#FFFFFF',
                      width: '350px',
                      height: '200px',
                      border: '1px solid #E9E9ED',
                      zIndex: 1,
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <img src={empty_state_clicks_iframe_icon} alt="Empty state" style={{ width: '100px', height: 'auto' }} />
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                      <h4 style={{ color: '#71717A' }}>No clicks in the last 30 days</h4>
                    </div>
                    <Button variant="primary" style={{ marginTop: '10px' }} onClick={openCampaign}>
                      Activate Campaign
                    </Button>
                  </div>
                </div>
                <div style={{ padding: '24px 24px 0px 24px' }}>
                  <h5 style={{ color: '#71717A', fontSize: '14px', fontWeight: '500' }}>Clicks (30d)</h5>
                  <h3 className="mb-0 mt-1" style={{ color:'#18181B', fontSize: '20px', fontWeight: '400' }}>
                    0
                  </h3>
                </div>
                <div className="w-100" style={{ padding: '0px 10px 10px 0px' }}>
                  <ReactApexChart
                    series={[{ name: 'Clicks', type: 'bar', data: Array(30).fill(0) }]}
                    options={optionFour}
                    type="bar"
                    height={300}
                    style={{ margin: '0 0 0 0', paddingLeft: '-10px', paddingRight: '-10px' }}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </React.Fragment>
      );
    }

    return (
      <React.Fragment style={{ 
        backgroundColor: 'rgba(232, 234, 237)',
      }}>
        <Col md="12" xl="12">
          <Card className="card-one" style={{ 
            borderColor: '#E9E9ED', 
            shadow: '0px 1px 2px rgba(0, 0, 0, 0.06)',  
            borderRadius: '8px',
            padding: '0px',
            height: 'max-content',
            border: '1px solid #E9E9ED',
            backgroundColor: '#FFFFFF',
          }}>
            <Card.Body style={{ padding: '0px' }}>
              <div style={{ padding: '24px 24px 0px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px' }}>
                <div style={{width: 'max-content'}}>
                  <h5 style={{ color: '#71717A', fontSize: '14px', fontWeight: '500', minWidth: '74px' }}>Clicks (30d)</h5>
                  <h3 className="mb-0 mt-1" style={{ color:'#18181B', fontSize: '20px', fontWeight: '400' }}>
                    {formatThousand(totalClicks)}
                  </h3>
                </div>
                { !campaign?.ppc_paused && (
                  <IframeBudgetAlert alertText={alertText} alertTitle={alertTitle} openCampaign={openCampaign} />
                )}
                {/* <IframeBudgetAlert alertText={alertText} alertTitle={alertTitle} openCampaign={openCampaign} /> */}
              </div>
              <div className="w-100" style={{ padding: '0px 10px 10px 0px' }}>
                <ReactApexChart
                  series={response.stats}
                  options={optionFour}
                  type="bar"
                  height={300}
                  style={{ margin: '0 0 0 0', paddingLeft: '-10px', paddingRight: '-10px' }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </React.Fragment>
    );
  } else {
    if (isLoading) return (
      <React.Fragment>
        <Placeholder
          as={Card.Title}
          animation="glow"
          style={{ color: "var(--bs-body-color)" }}
        >
          <Placeholder style={{ height: 387, width: "100%" }} />
        </Placeholder>
      </React.Fragment>
    );

    if (!response.success) {
      return modules(publisherId, blankPageMap)
    } else {
      return (
        <React.Fragment>
          <Placeholder
            as={Card.Title}
            animation="glow"
            style={{ color: "var(--bs-body-color)" }}
          >
            <Placeholder style={{ height: 387, width: "100%" }} />
          </Placeholder>
        </React.Fragment>
      );
    }
  }
}
